/**
=========================================================
* Argon Dashboard 2 MUI - v3.0.1
=========================================================

* Product Page: https://www.creative-tim.com/product/argon-dashboard-material-ui
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// @mui material components

import Card from "@mui/material/Card";

// Argon Dashboard 2 MUI components
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";

// Argon Dashboard 2 MUI examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import Table from "examples/Tables/Table";
import Grid from "@mui/material/Grid";
import dayjs from "dayjs";
import CustomDatePicker from "components/CustomDatePicker/CustomDatePicker";
import { Dialog, DialogTitle, DialogContent, DialogActions, MenuItem } from "@mui/material";
import ApnaTabs from "components/ApnaTabs/ApnaTabs";

import DraftTutorePayrollTableData from "Views/FinanceManagment/DraftTutorSalary/data/draftTutorSalaryTableData";
import { useState, useEffect } from "react";
import ArgonInput from "components/ArgonInput";
import ArgonButton from "components/ArgonButton";
import { set } from "date-fns";
import Dropdown from "components/CustomDropdown/Dropdown";
import DeleteDialog from "components/DeleteDialog/DeleteDialog";
import { getAllTutorPaymentCycles, updateTutorPaymentCycleStatus } from "services/financeService";
import { toast } from "react-toastify";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import team2 from "assets/images/team-2.jpg";
import team3 from "assets/images/team-3.jpg";
import ArgonAvatar from "components/ArgonAvatar";
import ArgonBadge from "components/ArgonBadge";
import PropTypes from "prop-types";
import PaginationBar from "components/PaginationBar/PaginationBar";

function Author({ image, name, email, mobile }) {
  return (
    <ArgonBox display="flex" alignItems="center" px={0.5} py={0.1}>
      <ArgonBox mr={2}>
        <ArgonAvatar src={image} alt={name} size="sm" variant="full" />
      </ArgonBox>
      <ArgonBox display="flex" flexDirection="column">
        <ArgonTypography variant="button" fontWeight="medium">
          {name}
        </ArgonTypography>
        <ArgonTypography variant="caption" color="secondary">
          {email}
        </ArgonTypography>
        <ArgonTypography variant="caption" color="secondary">
          {mobile}
        </ArgonTypography>
      </ArgonBox>
    </ArgonBox>
  );
}

function StudentData({ student, client }) {
  return (
    <ArgonBox display="flex" flexDirection="column">
      <ArgonTypography variant="caption" color="text" fontWeight="medium">
        {student}
      </ArgonTypography>
      <ArgonTypography variant="caption" color="text" fontWeight="medium">
        Client-{client}
      </ArgonTypography>
    </ArgonBox>
  );
}

Author.propTypes = {
  image: PropTypes.string,
  name: PropTypes.string,
  email: PropTypes.string,
  mobile: PropTypes.string,
};

StudentData.propTypes = {
  student: PropTypes.string,
  client: PropTypes.string,
};

function Tables() {
  const [dialog, setDeleteDialog] = useState(false);
  const handleDeleteClick = () => {
    setDeleteDialog(true);
  };
  const handleCloseDialog = () => {
    setDeleteDialog(false);
  };

  const [activeTab, setActiveTab] = useState("PENDING");
  const [statusCounts, setStatusCounts] = useState({
    DRAFT: 0,
    PENDING: 0,
    APPROVED: 0,
    REJECTED: 0,
    PAID: 0,
  });

  const getTabData = () => {
    return [
      {
        label: "Pending",
        count: statusCounts.PENDING,
        content: "",
        status: "PENDING",
      },
      {
        label: "Draft",
        count: statusCounts.DRAFT,
        content: "",
        status: "DRAFT",
      },
      {
        label: "Approved",
        count: statusCounts.APPROVED,
        content: "",
        status: "APPROVED",
      },
      {
        label: "Rejected",
        count: statusCounts.REJECTED,
        content: "",
        status: "REJECTED",
      },
      {
        label: "Paid",
        count: statusCounts.PAID,
        content: "",
        status: "PAID",
      },
    ];
  };

  const handleTabChange = (status) => {
    setActiveTab(status);
  };
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [search, setSearch] = useState("");

  const [selectedTutor, setSelectedTutor] = useState("");
  const [classList, setClassList] = useState("");
  const [cycle, setcycle] = useState("");

  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalRecords: 0,
    limit: 10,
  });

  // Handle Add Button Click

  const [open, setopen] = useState(false);

  const handleOpen = () => {
    setopen(true);
  };
  const handleClose = () => {
    setopen(false);
  };

  // Handle Update Dialog
  const [updateOpen, setUpdateOpen] = useState(false);
  const [selectedCycle, setSelectedCycle] = useState(null);
  const [updateForm, setUpdateForm] = useState({
    bonusAmount: 0,
    deductionAmount: 0,
    transactionId: "",
    remark: "",
    status: "APPROVED",
    paymentDate: null,
  });
  const [totalAmount, setTotalAmount] = useState(0);

  const handleUpdateOpen = (invoice) => {
    console.log("Invoice data:", invoice); // Debug log
    setSelectedCycle(invoice);
    let defaultStatus = invoice.status || "PENDING";
    if (invoice.status === "DRAFT" || invoice.status === "PENDING") {
      defaultStatus = "APPROVED";
    } else if (invoice.status === "APPROVED") {
      defaultStatus = "PAID";
    }
    setUpdateForm({
      bonusAmount: invoice.bonusAmount || 0,
      deductionAmount: invoice.deductionAmount || 0,
      transactionId: invoice.transactionId || "",
      remark: invoice.notes || "",
      status: defaultStatus,
      paymentDate: invoice.paymentDate ? dayjs(invoice.paymentDate) : dayjs(),
    });
    // Calculate total with existing bonus and deduction
    const baseAmount = invoice.calculatedAmount || 0;
    const bonus = invoice.bonusAmount || 0;
    const deduction = invoice.deductionAmount || 0;
    setTotalAmount(baseAmount + bonus - deduction);
    setUpdateOpen(true);
  };

  const handleUpdateClose = () => {
    setUpdateOpen(false);
    setSelectedCycle(null);
    setUpdateForm({
      bonusAmount: 0,
      deductionAmount: 0,
      transactionId: "",
      remark: "",
      status: "APPROVED",
      paymentDate: null,
    });
  };

  const handleUpdateFormChange = (field, value) => {
    setUpdateForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleUpdateSubmit = async () => {
    try {
      if (!selectedCycle?._id) {
        toast.error("No cycle selected");
        return;
      }

      if (updateForm.status === "PAID" && !updateForm.paymentDate) {
        toast.error("Please select payment date for PAID status");
        return;
      }

      const payload = {
        status: updateForm.status,
        bonusAmount: parseFloat(updateForm.bonusAmount) || 0,
        deductionAmount: parseFloat(updateForm.deductionAmount) || 0,
        transactionId: updateForm.transactionId,
        notes: updateForm.remark,
        paymentDate:
          updateForm.status === "PAID" && updateForm.paymentDate
            ? updateForm.paymentDate.toDate().toISOString()
            : undefined,
      };

      await updateTutorPaymentCycleStatus(selectedCycle._id, payload);
      toast.success("Status updated successfully!");
      handleUpdateClose();
      fetchInvoices(pagination.currentPage);
    } catch (error) {
      console.error("Update error:", error);
      toast.error(error.message || "Failed to update status");
    }
  };

  const [dob, setDob] = useState(dayjs());

  const getStatusOptions = () => {
    const currentStatus = selectedCycle?.status;
    if (currentStatus === "DRAFT" || currentStatus === "PENDING") {
      return [{ value: "APPROVED", label: "APPROVED" }];
    } else if (currentStatus === "APPROVED") {
      return [
        { value: "PAID", label: "PAID" },
        { value: "REJECTED", label: "REJECTED" },
      ];
    } else {
      // For other statuses, allow all options (though this might not be needed)
      return [
        { value: "APPROVED", label: "APPROVED" },
        { value: "REJECTED", label: "REJECTED" },
        { value: "PAID", label: "PAID" },
        { value: "DRAFT", label: "DRAFT" },
        { value: "PENDING", label: "PENDING" },
      ];
    }
  };

  // Fetch status counts for all tabs
  const fetchStatusCounts = async () => {
    try {
      const statuses = ["DRAFT", "PENDING", "APPROVED", "REJECTED", "PAID"];
      const counts = {};

      for (const status of statuses) {
        const response = await getAllTutorPaymentCycles({
          page: 1,
          limit: 1,
          status,
        });
        counts[status] = response.pagination?.totalRecords || 0;
      }

      setStatusCounts(counts);
    } catch (err) {
      console.error("Failed to fetch status counts:", err);
    }
  };

  // Fetch invoices from API
  const fetchInvoices = async (page = 1, limit = pagination.limit) => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        page,
        limit,
      };
      if (fromDate) params.from = fromDate.format("YYYY-MM-DD");
      if (toDate) params.to = toDate.format("YYYY-MM-DD");
      if (search) params.search = search;
      if (selectedTutor) params.tutorId = selectedTutor;
      if (cycle) params.cycleType = cycle;
      params.status = activeTab; // Filter based on active tab
      const response = await getAllTutorPaymentCycles(params);
      setInvoices(response.data || []);
      setPagination(
        response.pagination || {
          currentPage: 1,
          totalPages: 1,
          totalRecords: 0,
          limit: 10,
        }
      );
    } catch (err) {
      setError(err.message || "Failed to fetch invoices");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInvoices();
    fetchStatusCounts();
  }, [fromDate, toDate, activeTab, search]);

  // Auto-calculate total amount when bonus or deduction changes
  useEffect(() => {
    if (selectedCycle) {
      const baseAmount = selectedCycle.calculatedAmount || 0;
      const bonus = parseFloat(updateForm.bonusAmount) || 0;
      const deduction = parseFloat(updateForm.deductionAmount) || 0;
      setTotalAmount(baseAmount + bonus - deduction);
    }
  }, [updateForm.bonusAmount, updateForm.deductionAmount, selectedCycle]);

  // Pagination handlers
  const handlePageChange = (event, page) => {
    fetchInvoices(page);
  };

  const handleRowsPerPageChange = (event) => {
    const newLimit = event.target.value;
    setPagination((prev) => ({ ...prev, limit: newLimit, currentPage: 1 }));
    fetchInvoices(1, newLimit);
  };

  const columns = [
    { name: "S.no", align: "center" },
    { name: "tutor Name (Mobile)", align: "left" },
    { name: "Student", align: "left" },
    { name: "payment amount", align: "left" },
    { name: "status", align: "center" },
    { name: "date and time send", align: "left" },
    { name: "action", align: "center" },
  ];

  const rows = invoices.map((invoice, index) => ({
    "S.no": (
      <ArgonTypography variant="caption" color="text" fontWeight="medium">
        {(pagination.currentPage - 1) * pagination.limit + index + 1}
      </ArgonTypography>
    ),
    "tutor Name (Mobile)": (
      <Author
        image={invoice.tutorId?.image || invoice.tutorId?.profileImage || team2}
        name={invoice.tutorId?.fullName || "N/A"}
        mobile={invoice.tutorId?.phone || ""}
      />
    ),
    Student: (
      <StudentData
        student={invoice.financeId?.students?.map((s) => s.fullName || s.name).join(", ") || "N/A"}
        client={invoice.financeId?.parentId?.fullName || "N/A"}
      />
    ),
    "payment amount": (
      <ArgonTypography variant="caption" color="text" fontWeight="medium">
        ₹{invoice.calculatedAmount || 0}
      </ArgonTypography>
    ),
    status: (
      <ArgonBadge
        variant="gradient"
        badgeContent={invoice.status || "N/A"}
        color={
          invoice.status === "APPROVED" || invoice.status === "PAID"
            ? "success"
            : invoice.status === "DRAFT" || invoice.status === "PENDING"
            ? "warning"
            : invoice.status === "REJECTED" || invoice.status === "FAILED"
            ? "error"
            : "secondary"
        }
        size="xs"
        container
      />
    ),
    "date and time send": (
      <ArgonTypography variant="caption" color="text" fontWeight="medium">
        {dayjs(invoice.createdAt).format("DD MMM YYYY, hh:mm A")}
      </ArgonTypography>
    ),
    action: (
      <ArgonBox display="flex" gap={1} justifyContent="center">
        <EditIcon
          sx={{ cursor: "pointer", color: "#1976d2", fontSize: 20 }}
          onClick={() => handleUpdateOpen(invoice)}
        />
      </ArgonBox>
    ),
  }));

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <ArgonBox py={3}>
        <ArgonBox mb={3}>
          <Card>
            <ArgonBox p={3}>
              <ArgonTypography mb={2} variant="h6" fontWeight="medium">
                Draft Tutors Salary List
              </ArgonTypography>

              {/* Tabs for Status Filter */}
              <ArgonBox sx={{ width: "fit-content", mb: 2 }}>
                <ApnaTabs tabsData={getTabData()} onTabChange={handleTabChange} />
              </ArgonBox>

              <ArgonBox p={2}>
                <ArgonBox
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  flexWrap="wrap"
                  gap={2} // equal horizontal + vertical gap
                >
                  <ArgonBox display="flex" alignItems="center" gap={2}>
                    <ArgonTypography variant="body2" fontWeight="medium" color="text">
                      Filters
                    </ArgonTypography>
                    <CustomDatePicker
                      label="From"
                      value={fromDate}
                      onChange={(newValue) => setFromDate(newValue)}
                      width={160}
                      maxDate={dayjs()} // future date disable
                    />
                    <CustomDatePicker
                      label="To"
                      value={toDate}
                      onChange={(newValue) => setToDate(newValue)}
                      width={160}
                      maxDate={dayjs()} // future date disable
                      minDate={fromDate}
                    />
                  </ArgonBox>

                  <ArgonBox display="flex" alignItems="center" gap={2}>
                    {/* Search Field */}
                    <ArgonInput
                      fullWidth
                      size="small"
                      placeholder="Search by Name, Phone or Email"
                      value={search}
                      onChange={(e) => {
                        setSearch(e.target.value);
                        fetchInvoices(1);
                      }}
                    />

                    {/* Reset Button */}
                    <ArgonButton
                      variant="outlined"
                      size="small"
                      color="secondary"
                      sx={{ minWidth: "100px" }}
                      onClick={() => {
                        setSearch("");
                        setFromDate(null);
                        setToDate(null);
                        fetchInvoices(1);
                      }}
                    >
                      Reset
                    </ArgonButton>

                    {/* Add Button */}
                    {/* <ArgonButton
      onClick={handleOpen}
      variant="contained"
      size="small"
      color="primary"
      sx={{ minWidth: "100px" }}
    >
      Add
    </ArgonButton> */}
                  </ArgonBox>
                </ArgonBox>
              </ArgonBox>
            </ArgonBox>

            <ArgonBox
              sx={{
                "& .MuiTableRow-root:not(:last-child)": {
                  "& td": {
                    borderBottom: ({ borders: { borderWidth, borderColor } }) =>
                      `${borderWidth[1]} solid ${borderColor}`,
                  },
                },
              }}
            >
              {loading ? (
                <ArgonTypography variant="h6" color="text" align="center">
                  Loading...
                </ArgonTypography>
              ) : error ? (
                <ArgonTypography variant="h6" color="error" align="center">
                  {error}
                </ArgonTypography>
              ) : invoices.length > 0 ? (
                <Table columns={columns} rows={rows} />
              ) : (
                <ArgonBox textAlign="center" py={4}>
                  <ArgonTypography variant="h6" color="text" fontWeight="medium">
                    No Tutors Salary List found
                  </ArgonTypography>
                  <ArgonTypography variant="caption" color="secondary">
                    Try adjusting your filters or check back later.
                  </ArgonTypography>
                </ArgonBox>
              )}
            </ArgonBox>

            {/* Pagination Controls */}
            {invoices.length > 0 && (
              <PaginationBar
                currentPage={pagination.currentPage}
                rowsPerPage={pagination.limit}
                totalItems={pagination.totalRecords}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleRowsPerPageChange}
              />
            )}
          </Card>
        </ArgonBox>
      </ArgonBox>

      {/* dialog */}

      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle sx={{ backgroundColor: "#f5f7fa", borderBottom: "1px solid #eee" }}>
          <ArgonTypography variant="h5" fontWeight="bold" color="primary">
            Tutor Draft Salary Add
          </ArgonTypography>
        </DialogTitle>

        <DialogContent dividers sx={{ backgroundColor: "#fafafa" }}>
          <Grid container spacing={1}>
            {/* Tutor */}
            <Grid item xs={12} md={6}>
              <ArgonTypography variant="subtitle2" fontWeight="medium" color="text" mb={1}>
                Tutor
              </ArgonTypography>
              <Dropdown
                value={selectedTutor}
                options={["John Doe", "Jane Smith", "Alice Johnson"]}
                onChange={(e) => setSelectedTutor(e.target.value)}
                width="100%"
              />
            </Grid>

            {/* Class */}
            <Grid item xs={12} md={6}>
              <ArgonTypography variant="subtitle2" fontWeight="medium" color="text" mb={1}>
                Class
              </ArgonTypography>
              <Dropdown
                value={classList}
                options={["Math Class", "Science Class", "English Class"]}
                onChange={(e) => setClassList(e.target.value)}
                width="100%"
              />
            </Grid>

            {/* Cycle */}
            <Grid item xs={12}>
              <ArgonTypography variant="subtitle2" fontWeight="medium" color="text" mb={1}>
                Cycle
              </ArgonTypography>
              <Dropdown
                value={cycle}
                options={["Cycle 1", "Cycle 2", "Cycle 3"]}
                onChange={(e) => setcycle(e.target.value)}
                width="100%"
              />
            </Grid>

            {/* Amount */}
            <Grid item xs={12} md={6}>
              <ArgonTypography variant="subtitle2" fontWeight="medium" color="text" mb={1}>
                Enter Amount
              </ArgonTypography>
              <ArgonInput fullWidth placeholder="Enter Amount" />
            </Grid>

            {/* Bonus Note */}
            <Grid item xs={12} md={6}>
              <ArgonTypography variant="subtitle2" fontWeight="medium" color="text" mb={1}>
                Bonus Note
              </ArgonTypography>
              <ArgonInput fullWidth placeholder="Enter Bonus Note" />
            </Grid>

            {/* Total Bonus */}
            <Grid item xs={12} md={6}>
              <ArgonTypography variant="subtitle2" fontWeight="medium" color="text" mb={1}>
                Total Bonus Amount
              </ArgonTypography>
              <ArgonInput fullWidth placeholder="Enter Bonus Amount" />
            </Grid>

            {/* Deduction Note */}
            <Grid item xs={12} md={6}>
              <ArgonTypography variant="subtitle2" fontWeight="medium" color="text" mb={1}>
                Deduction Note
              </ArgonTypography>
              <ArgonInput fullWidth placeholder="Enter Deduction Note" />
            </Grid>

            {/* Deduction Amount */}
            <Grid item xs={12} md={6}>
              <ArgonTypography variant="subtitle2" fontWeight="medium" color="text" mb={1}>
                Deduction Amount
              </ArgonTypography>
              <ArgonInput fullWidth placeholder="Enter Deduction Amount" />
            </Grid>

            {/* Cycle Completion Date */}
            <Grid item xs={12} md={6}>
              <ArgonTypography variant="subtitle2" fontWeight="medium" color="text" mb={1}>
                Cycle Completion Date
              </ArgonTypography>
              <CustomDatePicker
                value={dob}
                onChange={(newValue) => setDob(newValue)}
                width={220}
                maxDate={dayjs()}
              />
            </Grid>

            {/* Payment Due Date */}
            <Grid item xs={12} md={6}>
              <ArgonTypography variant="subtitle2" fontWeight="medium" color="text" mb={1}>
                Payment Due Date
              </ArgonTypography>
              <CustomDatePicker
                value={dob}
                onChange={(newValue) => setDob(newValue)}
                width={220}
                maxDate={dayjs()}
              />
            </Grid>

            {/* Remark */}
            <Grid item xs={12}>
              <ArgonTypography variant="subtitle2" fontWeight="medium" color="text" mb={1}>
                Remark
              </ArgonTypography>
              <ArgonInput fullWidth multiline rows={3} placeholder="Enter Remark" />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ p: 3, backgroundColor: "#f5f7fa" }}>
          <ArgonButton variant="outlined" color="secondary" onClick={handleClose}>
            Cancel
          </ArgonButton>
          <ArgonButton
            variant="gradient"
            color="info"
            sx={{ ml: 2, boxShadow: 3 }}
            onClick={handleClose}
          >
            Save
          </ArgonButton>
        </DialogActions>
      </Dialog>

      {/* Update Status Dialog */}
      <Dialog open={updateOpen} onClose={handleUpdateClose} maxWidth="md" fullWidth>
        <DialogTitle sx={{ backgroundColor: "#f5f7fa", borderBottom: "1px solid #eee" }}>
          <ArgonTypography variant="h5" fontWeight="bold" color="primary">
            Update Tutor Salary Status
          </ArgonTypography>
        </DialogTitle>

        <DialogContent dividers sx={{ backgroundColor: "#fafafa" }}>
          <Grid container spacing={2}>
            {/* Base Amount (Read-only) */}
            <Grid item xs={12} md={6}>
              <ArgonTypography variant="subtitle2" fontWeight="medium" color="text" mb={1}>
                Base Amount
              </ArgonTypography>
              <ArgonInput fullWidth value={`₹${selectedCycle?.calculatedAmount || 0}`} disabled />
            </Grid>

            {/* Bonus Amount */}
            <Grid item xs={12} md={6}>
              <ArgonTypography variant="subtitle2" fontWeight="medium" color="text" mb={1}>
                Bonus Amount
              </ArgonTypography>
              <ArgonInput
                fullWidth
                type="number"
                placeholder="Enter Bonus Amount"
                value={updateForm.bonusAmount}
                onChange={(e) => handleUpdateFormChange("bonusAmount", e.target.value)}
              />
            </Grid>

            {/* Deduction Amount */}
            <Grid item xs={12} md={6}>
              <ArgonTypography variant="subtitle2" fontWeight="medium" color="text" mb={1}>
                Deduction Amount
              </ArgonTypography>
              <ArgonInput
                fullWidth
                type="number"
                placeholder="Enter Deduction Amount"
                value={updateForm.deductionAmount}
                onChange={(e) => handleUpdateFormChange("deductionAmount", e.target.value)}
              />
            </Grid>

            {/* Total Amount (Auto-calculated) */}
            <Grid item xs={12} md={6}>
              <ArgonTypography variant="subtitle2" fontWeight="medium" color="text" mb={1}>
                Total Amount (Auto-calculated)
              </ArgonTypography>
              <ArgonInput
                fullWidth
                value={`₹${totalAmount.toFixed(2)}`}
                disabled
                sx={{ fontWeight: "bold" }}
              />
            </Grid>

            {/* Transaction ID */}
            <Grid item xs={12} md={6}>
              <ArgonTypography variant="subtitle2" fontWeight="medium" color="text" mb={1}>
                Transaction ID
              </ArgonTypography>
              <ArgonInput
                fullWidth
                placeholder="Enter Transaction ID"
                value={updateForm.transactionId}
                onChange={(e) => handleUpdateFormChange("transactionId", e.target.value)}
              />
            </Grid>

            {/* Status */}
            <Grid item xs={12} md={6}>
              <ArgonTypography variant="subtitle2" fontWeight="medium" color="text" mb={1}>
                Status
              </ArgonTypography>
              <Dropdown
                value={updateForm.status}
                options={getStatusOptions()}
                onChange={(e) => handleUpdateFormChange("status", e.target.value)}
                width="100%"
              />
            </Grid>

            {updateForm.status === "PAID" && (
              <Grid item xs={12} md={6}>
                <ArgonTypography variant="subtitle2" fontWeight="medium" color="text" mb={1}>
                  Payment Date
                </ArgonTypography>
                <CustomDatePicker
                  value={updateForm.paymentDate}
                  onChange={(newValue) => handleUpdateFormChange("paymentDate", newValue)}
                  width={220}
                  maxDate={dayjs()}
                />
              </Grid>
            )}

            {/* Remark */}
            <Grid item xs={12}>
              <ArgonTypography variant="subtitle2" fontWeight="medium" color="text" mb={1}>
                Remark
              </ArgonTypography>
              <ArgonInput
                fullWidth
                multiline
                rows={3}
                placeholder="Enter Remark"
                value={updateForm.remark}
                onChange={(e) => handleUpdateFormChange("remark", e.target.value)}
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ p: 3, backgroundColor: "#f5f7fa" }}>
          <ArgonButton variant="outlined" color="secondary" onClick={handleUpdateClose}>
            Cancel
          </ArgonButton>
          <ArgonButton
            variant="gradient"
            color="info"
            sx={{ ml: 2, boxShadow: 3 }}
            onClick={handleUpdateSubmit}
          >
            Update Status
          </ArgonButton>
        </DialogActions>
      </Dialog>

      {/* Deletedialog */}
      <DeleteDialog
        open={dialog}
        onClose={handleCloseDialog}
        onConfirm={handleCloseDialog}
        title="Delete Draft Tutor Salary"
        description="Are you sure you want to delete this draft tutor salary?"
        cancelText="Cancel"
        confirmText="Delete"
      />

      <Footer />
    </DashboardLayout>
  );
}

export default Tables;
